import React, { FC } from "react";
import MuiButton from "@mui/material/Button";

interface Props {
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  loading?: boolean;
  type?: string | any;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
  width?: string;
  height?: string;
  float?: string;
  // "primary" (filled blue) and "danger" (filled red) match the site's
  // existing Next/Search/Submit vs Cancel convention. "outlined" matches
  // the existing Back button style (white, primary-dark border/text).
  styleType?: "primary" | "danger" | "outlined" | string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  // Buttons placed in a row alongside others (Cancel/Next, Back/Submit)
  // don't want the standalone-submit-button default top margin.
  noMarginTop?: boolean;
  // Lets a caller render this as a router link (component={NavLink}
  // to="/somewhere") instead of a plain <button> - MUI renders the link
  // itself with button styling/behavior, rather than nesting a <button>
  // inside an <a> the way the old NavLink-wraps-button markup did.
  [key: string]: any;
}

export const Button: FC<Props> = ({
  styleType,
  type,
  onClick,
  loading,
  children,
  disabled,
  className,
  width,
  height,
  float,
  startIcon,
  endIcon,
  noMarginTop,
  sx,
  ...rest
}) => {
  const isOutlined = styleType === "outlined";
  return (
    <MuiButton
      {...rest}
      type={type as "button" | "submit" | "reset" | undefined}
      variant={isOutlined ? "outlined" : "contained"}
      disableElevation
      color={styleType === "danger" ? "error" : "primary"}
      onClick={onClick}
      disabled={disabled}
      className={className}
      startIcon={startIcon}
      endIcon={endIcon}
      style={{ float: (float as React.CSSProperties["float"]) ?? "none" }}
      sx={{
        // MUI Button's own root is display: inline-flex - keep it that
        // way (not "flex" or "block"): "flex" makes the button a
        // block-level box, so two buttons next to each other with no
        // explicit flex-row parent (e.g. the Yes/No pair) stack
        // vertically instead of sitting side by side. inline-flex keeps
        // the icon+label internal layout AND flows inline like the
        // original native <button> did.
        display: "inline-flex",
        width: width ?? "100%",
        // Every button defaults to the same height unless a caller needs
        // to match a specific neighbour's size (e.g. a search button
        // sitting in the same row as a text field).
        height: height ?? "42px",
        marginTop: noMarginTop ? 0 : "20px",
        ...sx,
      }}
    >
      {loading ? "loading..." : children}
    </MuiButton>
  );
};
